import { env } from "@/config/env";
import type { WazuhInstallCommands } from "../api/wazuhCustomerApi";

export type EmailRecipient = "admin-inbox" | "client-direct";
export type LinkValidity = "7" | "30" | "90" | "none";

export type NewCustomerCreationResult = {
  customerName: string;
  customerId: string;
  /** Organization UUID (`organization.id` from the create response) — needed by the
   * Wazuh groups proxy call, distinct from `customerId` (the tenant code). */
  organizationId: string;
  adminEmail: string;
  /** Assigned account manager shown in the welcome-email support line. */
  accountManagerName: string;
  accountManagerPhone: string;
  clientFullName: string;
  clientEmail: string;
  emailRecipient: EmailRecipient;
  linkValidity: LinkValidity;
  loginUrl: string;
  customerUrlHost: string;
  createdAt: string;
  /** Wazuh agent groups + per-OS install commands, fetched once right after customer
   * creation in Step 3. `null` if that provisioning call failed — Step 4 offers a retry
   * in that case rather than blocking the rest of onboarding. */
  installCommands: WazuhInstallCommands | null;
};

const STORAGE_KEY = "ns-new-customer-creation-result";

export function generateCustomerId(): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let i = 0; i < 4; i += 1) {
    suffix += chars[Math.floor(Math.random() * chars.length)]!;
  }
  return `TNT-${date}-${suffix}`;
}

/**
 * Env-specific client portal welcome URL (no one-time token).
 * Prefer the backend-issued `welcome_url` when available; use this for display
 * fallbacks when the opaque token cannot be reconstructed.
 */
export function buildClientPortalWelcomeUrl(): string {
  return `${env.clientAppBaseUrl}/welcome`;
}

/** @deprecated Prefer {@link buildClientPortalWelcomeUrl} or the API `welcome_url`. */
export function buildWelcomeLoginUrl(_host: string, _customerId: string): string {
  return buildClientPortalWelcomeUrl();
}

/**
 * The backend still issues a real one-time welcome token as a `?token=...` query
 * param, but admins shouldn't see (or copy/forward) that raw secret — strip it for
 * display purposes only. The underlying token generation/consumption is unchanged.
 */
export function displayWelcomeLoginUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = "";
    return parsed.toString();
  } catch {
    return url.split("?")[0] ?? url;
  }
}

export function linkValidityLabel(validity: LinkValidity): string {
  switch (validity) {
    case "7":
      return "7 days from receipt";
    case "30":
      return "30 days from receipt";
    case "90":
      return "90 days from receipt";
    case "none":
      return "No expiry";
  }
}

export function saveNewCustomerCreationResult(
  result: NewCustomerCreationResult,
): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
  } catch {
    /* quota / private mode */
  }
}

export function getNewCustomerCreationResult(): NewCustomerCreationResult | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as NewCustomerCreationResult;
    if (!parsed?.customerId || !parsed?.clientEmail || !parsed?.organizationId) {
      return null;
    }
    return {
      ...parsed,
      accountManagerName:
        parsed.accountManagerName?.trim() ||
        parsed.adminEmail?.split("@")[0] ||
        "your account manager",
      accountManagerPhone: parsed.accountManagerPhone?.trim() || "",
    };
  } catch {
    return null;
  }
}

export function clearNewCustomerCreationResult(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function formatWelcomeSupportSentence(
  accountManagerName: string,
  accountManagerPhone?: string | null,
): string {
  const managerName =
    accountManagerName.trim() || "your account manager";
  const managerPhone = accountManagerPhone?.trim() || "";
  if (managerPhone) {
    return `For onboarding support, your account manager ${managerName} is available — reply to this email or call ${managerPhone}.`;
  }
  return `For onboarding support, your account manager ${managerName} is available — reply to this email.`;
}

export function buildWelcomeEmailPlainTextFromFields(input: {
  clientFullName: string;
  clientEmail: string;
  loginUrl: string;
  validityLabel: string;
  accountManagerName: string;
  accountManagerPhone?: string | null;
  /** @deprecated Kept optional so older call sites compile; ignored for support contact. */
  adminEmail?: string;
}): string {
  return [
    `Dear ${input.clientFullName},`,
    "",
    "Welcome to NetShield — AI-based Risk Intelligence Platform by NetShield Technologies. Your customer tenant has been provisioned. Below is the link to access your dashboard.",
    "",
    `Login URL: ${displayWelcomeLoginUrl(input.loginUrl)}`,
    `Email to sign in with: ${input.clientEmail}`,
    `Link valid for: ${input.validityLabel}`,
    "",
    "How sign-in works. NetShield does not use passwords for customer access. When you click the link above and enter your email, we'll send a 6-digit one-time password (OTP) to that email. Enter the OTP to sign in. The OTP is valid for 10 minutes; you can request a new one any time.",
    "",
    formatWelcomeSupportSentence(
      input.accountManagerName,
      input.accountManagerPhone,
    ),
    "",
    "Welcome aboard.",
    "— The NetShield Team",
  ].join("\n");
}

export function buildWelcomeEmailPlainText(
  result: NewCustomerCreationResult,
): string {
  return buildWelcomeEmailPlainTextFromFields({
    clientFullName: result.clientFullName,
    clientEmail: result.clientEmail,
    loginUrl: result.loginUrl,
    validityLabel: linkValidityLabel(result.linkValidity),
    accountManagerName: result.accountManagerName,
    accountManagerPhone: result.accountManagerPhone,
    adminEmail: result.adminEmail,
  });
}

export function formatCreationTimestamp(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
